import { Link } from "react-router-dom";
import type { Visibility } from "../../api/types";
import { VisibilityBadge } from "../VisibilityBadge";

/**
 * A single frame in a thumbnail grid, used for both photographs and films.
 * Shows a frame number and accession code along the top (like a real contact
 * sheet), the image, and an optional play glyph for films. The whole frame is a
 * link to the item's document page (where it can be viewed full size and
 * commented on).
 */
interface ContactFrameProps {
  /** 1-based position on the sheet, shown as the frame number. */
  index: number;
  /** Accession code, e.g. "PH-0007". */
  code: string;
  title: string;
  /** Secondary caption line (date, duration, etc.). */
  caption?: string;
  imageUrl: string | null;
  /** Show the play glyph overlay (films). */
  isVideo?: boolean;
  /** Internal route to the item's document page. */
  to: string;
  /** Marks a frame that is not public (only members and the admin see these). */
  visibility?: Visibility;
}

export function ContactFrame({
  index,
  code,
  title,
  caption,
  imageUrl,
  isVideo = false,
  to,
  visibility,
}: ContactFrameProps) {
  const frameNo = String(index).padStart(2, "0");

  return (
    <Link className="frame" to={to} aria-label={`${title} — open`}>
      <div className="frame__num">
        <span>{frameNo}</span>
        <span>{code}</span>
      </div>

      <div className="frame__media">
        {imageUrl ? (
          <img src={imageUrl} alt={title} loading="lazy" />
        ) : (
          <div style={{ width: "100%", height: "100%" }} aria-hidden="true" />
        )}
        {isVideo && (
          <span className="frame__play" aria-hidden="true">
            ►
          </span>
        )}
        {visibility && visibility !== "PUBLIC" && (
          <span className="frame__vis">
            <VisibilityBadge visibility={visibility} />
          </span>
        )}
      </div>

      <div className="frame__caption">
        {title}
        {caption && <span>{caption}</span>}
      </div>
    </Link>
  );
}
